// src/pages/DietRecommend.js — 식단 추천 (끼니·지역 선택 → 목표 맞춤 상차림 → 기록). 관리자는 음식풀 관리 탭
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDietRecommendV2, getUserInfo } from '../api/dietApi';
import { addFoodsToDay, mealByHour, todayStr } from '../api/dietDay';
import EmptyState from '../components/ui/EmptyState';
import { MACROS, MacroText, NutrientBar, fmt } from '../components/ui/Nutrients';
import { useToast } from '../components/ui/Toast';
import PoolAdmin from './PoolAdmin';
import './DietRecommend.css';

const MEALS = [{ code: 'BR', label: '아침' }, { code: 'LC', label: '점심' }, { code: 'DN', label: '저녁' }, { code: 'SN', label: '간식' }];
const REGIONS = [
  { code: 'ALL', label: '전체' }, { code: 'KR', label: '한식' }, { code: 'WS', label: '양식' }, { code: 'JP', label: '일식' },
  { code: 'CH', label: '중식' }, { code: 'AS', label: '동남아식' }, { code: 'ET', label: '기타' },
];
const hasGoal = (u) => u && [u.wantKcal, u.carboRate, u.protRate, u.fatRate].every((v) => v !== null && v !== undefined && v !== '');

export default function DietRecommend() {
  const navigate = useNavigate();
  const toast = useToast();
  const [user, setUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [tab, setTab] = useState('recommend');
  const [meal, setMeal] = useState(mealByHour());
  const [region, setRegion] = useState('ALL');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [date, setDate] = useState(todayStr());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getUserInfo().then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setUserLoaded(true));
  }, []);

  const admin = user?.userRole === 'ADMIN';
  const goalOk = hasGoal(user);

  const recommend = async (m = meal, rg = region) => {
    setLoading(true); setError('');
    try {
      const res = await getDietRecommendV2(m, rg);
      setResult(res.data);
    } catch (e) {
      setResult(null);
      setError(e?.response?.data?.message || '추천 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 목표가 있으면 들어오자마자 한 번 추천
  useEffect(() => {
    if (userLoaded && goalOk) recommend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoaded, goalOk]);

  const pickMeal = (m) => { setMeal(m); recommend(m, region); };
  const pickRegion = (rg) => { setRegion(rg); recommend(meal, rg); };

  const record = async () => {
    setSaving(true);
    try {
      await addFoodsToDay(date, result.dietCd, result.foods.map((f) => ({ foodCd: f.foodCd, intake: f.intake })));
      toast(`${date.slice(5).replace('-', '/')} ${result.dietNm}에 추천 식단을 기록했어요.`, {
        type: 'success', action: { label: '식단 보기', onClick: () => navigate(`/diet/${date}`) },
      });
    } catch (e) {
      toast(e?.response?.data?.message || '기록 중 오류가 발생했습니다.', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!userLoaded) return <p className="rc-msg">불러오는 중…</p>;

  return (
    <div className="rc">
      {admin && (
        <div className="rc-tabs">
          <button type="button" className={tab === 'recommend' ? 'on' : ''} onClick={() => setTab('recommend')}>식단 추천</button>
          <button type="button" className={tab === 'pool' ? 'on' : ''} onClick={() => setTab('pool')}>음식풀 관리</button>
        </div>
      )}

      {tab === 'pool' && admin ? <PoolAdmin /> : !goalOk ? (
        <div className="dash-goal-empty">
          <div>
            <strong>영양 목표를 먼저 설정해 주세요</strong>
            <p>희망 칼로리와 탄수화물·단백질·지방 비율을 입력하면 끼니 목표에 맞춘 식단을 추천해 드려요.</p>
          </div>
          <button className="btn primary" type="button" onClick={() => navigate('/mypage')}>마이페이지에서 목표 입력</button>
        </div>
      ) : (
        <>
          <div className="rc-controls">
            <div className="rc-seg" role="group" aria-label="끼니">
              {MEALS.map((m) => (
                <button key={m.code} type="button" className={meal === m.code ? 'on' : ''} onClick={() => pickMeal(m.code)}>{m.label}</button>
              ))}
            </div>
            <div className="rc-chips" role="group" aria-label="지역">
              {REGIONS.map((r) => (
                <button key={r.code} type="button" className={`chip${region === r.code ? ' on' : ''}`} onClick={() => pickRegion(r.code)}>{r.label}</button>
              ))}
            </div>
          </div>

          {error ? (
            <EmptyState message={error} actionLabel="다시 추천" onAction={() => recommend()} />
          ) : !result ? (
            <p className="rc-msg">{loading ? '추천 중…' : ''}</p>
          ) : (
            <div className={`rc-card${loading ? ' loading' : ''}`}>
              <header className="rc-card-head">
                <div>
                  <span className="rc-badge">{result.regionNm}</span>
                  <h2>{result.dietNm} 추천</h2>
                </div>
                <button className="btn" type="button" disabled={loading} onClick={() => recommend()}>{loading ? '추천 중…' : '↻ 다시 추천'}</button>
              </header>

              <ul className="rc-foods">
                {result.foods.map((f) => (
                  <li key={f.foodCd}>
                    <span className="rc-pos">{f.foodPosNm}</span>
                    <div className="rc-food-main">
                      <strong>{f.foodNm}</strong>
                      <MacroText carbo={f.carbo} protein={f.protein} fat={f.fat} />
                    </div>
                    <span className="rc-g">{fmt(f.intake)}g</span>
                    <span className="rc-kcal">{fmt(f.kcal)} kcal</span>
                  </li>
                ))}
              </ul>

              <div className="rc-total">
                <div className="rc-total-kcal">
                  <strong>{fmt(result.total.kcal)}</strong>
                  <span>/ {fmt(result.target.kcal)} kcal · 목표의 {result.rate.kcal}%</span>
                </div>
                <div className="rc-bars">
                  {MACROS.map((m) => (
                    <NutrientBar key={m.key} label={m.label} color={m.color} value={result.total[m.key]} goal={result.target[m.key]} />
                  ))}
                </div>
                <p className="rc-note">한 끼 목표 = 하루 희망 칼로리 × 끼니 비중 (아침 25% · 점심 35% · 저녁 30% · 간식 10%)</p>
              </div>

              <footer className="rc-record">
                <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                <button className="btn primary" type="button" disabled={saving || !date} onClick={record}>
                  {saving ? '기록 중…' : `이 식단을 ${result.dietNm}으로 기록하기`}
                </button>
              </footer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
